/**
 * Consultas e gravações do quadro de colaboradores pelo TanStack Query.
 *
 * Papel: aqui ficam `CHAVE_RH`, o prefixo de toda chave do módulo, e
 * `useColaboradores` com as duas mutações do quadro, que gravam e mandam a
 * lista se atualizar.
 *
 * Como usar: o banco de horas tem a lista própria de colaboradores e os hooks
 * dele em `usar-banco-horas.ts`; gravar no quadro não mexe no banco de horas,
 * e por isso as mutações daqui invalidam só `CHAVE_COLABORADORES`.
 */
import { type NovoColaborador } from '@concierge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  atualizarColaborador,
  buscarColaboradores,
  criarColaborador,
  excluirColaborador,
} from '../api/colaboradores';

/** Prefixo de todas as chaves de cache deste módulo. */
export const CHAVE_RH = ['rh'] as const;

/** Chave da lista de colaboradores. */
export const CHAVE_COLABORADORES = [...CHAVE_RH, 'colaboradores'] as const;

/**
 * Lê os colaboradores cadastrados.
 *
 * @returns Resultado da consulta, com a residência aninhada em cada um.
 */
export function useColaboradores() {
  return useQuery({
    queryKey: CHAVE_COLABORADORES,
    queryFn: ({ signal }) => buscarColaboradores(signal),
  });
}

/** Variáveis de `useSalvarColaborador`. */
interface SalvarColaboradorVariaveis {
  /** Identificador do colaborador a alterar, ou `null` para criar um novo. */
  id: string | null;
  dados: NovoColaborador;
}

/**
 * Cria ou altera um colaborador, conforme venha identificador.
 *
 * Invalida só `CHAVE_COLABORADORES`: o banco de horas tem a lista própria de
 * colaboradores, e gravar no quadro não altera o que ele mostra.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a gravação não responde.
 */
export function useSalvarColaborador() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dados }: SalvarColaboradorVariaveis) =>
      id === null ? criarColaborador(dados) : atualizarColaborador(id, dados),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_COLABORADORES }),
  });
}

/**
 * Apaga um colaborador.
 *
 * Rejeita com 409 quando o calendário, a escala ou uma ausência ainda cita a pessoa, e nesse caso
 * nada foi apagado — a tela precisa dizer isso a quem tentou.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a exclusão não responde.
 */
export function useExcluirColaborador() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => excluirColaborador(id),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_COLABORADORES }),
  });
}
